import { isHexColor } from "@/config/themes/brandColors";
import type {
  AppKpiChange,
  AppKpiChangeKind,
  AppKpiColorRule,
  AppKpiDisplayConfig,
  AppKpiFontSize,
  AppKpiFormat,
  AppKpiRuleOperator,
} from "@/features/app-studio/api/appKpisApi";

export const KPI_FONT_SIZE_OPTIONS: readonly AppKpiFontSize[] = ["sm", "md", "lg", "xl"];

/** The size a card uses when none is set; picking it stores nothing. */
export const KPI_DEFAULT_FONT_SIZE: AppKpiFontSize = "md";

export const KPI_CHANGE_KIND_OPTIONS: readonly AppKpiChangeKind[] = [
  "percent",
  "amount",
  "previous",
];

/** What a chip's `kind` means when none is stored; picking it stores nothing. */
export const KPI_DEFAULT_CHANGE_KIND: AppKpiChangeKind = "percent";

export const KPI_RULE_OPERATOR_OPTIONS: readonly AppKpiRuleOperator[] = [
  "gt",
  "gte",
  "lt",
  "lte",
  "eq",
  "between",
];

/**
 * One colour rule as the editor holds it: numbers as typed text, so a half-typed "-" or "1." is
 * not lost, and colours as `""` for "not set".
 */
export type KpiColorRuleDraft = {
  /** Stable React key; not stored. */
  key: number;
  op: AppKpiRuleOperator;
  value: string;
  valueTo: string;
  cardColor: string;
  textColor: string;
};

/** The change chip as the editor holds it. `column: ""` means no chip. */
export type KpiChangeDraft = {
  column: string;
  kind: AppKpiChangeKind;
  /** `""` keeps the theme's colour for that direction. */
  increaseColor: string;
  decreaseColor: string;
  size: AppKpiFontSize;
  label: string;
};

/** A KPI card's look as the editor holds it. `""` colours mean the default. */
export type KpiStyleDraft = {
  cardColor: string;
  textColor: string;
  valueFontSize: AppKpiFontSize;
  labelFontSize: AppKpiFontSize;
  rules: KpiColorRuleDraft[];
  change: KpiChangeDraft;
};

/** What is wrong with a rule, so the editor can mark the right field. */
export type KpiColorRuleIssue = "value" | "valueTo" | "range" | "color";

export function emptyKpiStyleDraft(): KpiStyleDraft {
  return {
    cardColor: "",
    textColor: "",
    valueFontSize: KPI_DEFAULT_FONT_SIZE,
    labelFontSize: KPI_DEFAULT_FONT_SIZE,
    rules: [],
    change: emptyKpiChangeDraft(),
  };
}

export function emptyKpiChangeDraft(): KpiChangeDraft {
  return {
    column: "",
    kind: KPI_DEFAULT_CHANGE_KIND,
    increaseColor: "",
    decreaseColor: "",
    size: KPI_DEFAULT_FONT_SIZE,
    label: "",
  };
}

export function newKpiColorRuleDraft(key: number): KpiColorRuleDraft {
  return { key, op: "gt", value: "", valueTo: "", cardColor: "", textColor: "" };
}

function isFontSize(value: unknown): value is AppKpiFontSize {
  return typeof value === "string" && (KPI_FONT_SIZE_OPTIONS as readonly string[]).includes(value);
}

function isRuleOperator(value: unknown): value is AppKpiRuleOperator {
  return (
    typeof value === "string" && (KPI_RULE_OPERATOR_OPTIONS as readonly string[]).includes(value)
  );
}

function isChangeKind(value: unknown): value is AppKpiChangeKind {
  return (
    typeof value === "string" && (KPI_CHANGE_KIND_OPTIONS as readonly string[]).includes(value)
  );
}

/** The stored chip if it names a column; anything else means no chip. */
function storedChange(config: AppKpiDisplayConfig | null | undefined): AppKpiChange | null {
  const raw: unknown = config?.change;
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const column = (raw as Record<string, unknown>).column;
  return typeof column === "string" && column.trim() !== "" ? (raw as AppKpiChange) : null;
}

function hexOrNull(value: unknown): string | null {
  return typeof value === "string" && isHexColor(value) ? value : null;
}

/** Stored rules that are well-formed; anything else is skipped rather than trusted. */
function storedRules(config: AppKpiDisplayConfig | null | undefined): AppKpiColorRule[] {
  const raw: unknown = config?.rules;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (rule): rule is AppKpiColorRule =>
      typeof rule === "object" &&
      rule !== null &&
      isRuleOperator((rule as Record<string, unknown>).op) &&
      typeof (rule as Record<string, unknown>).value === "number",
  );
}

export function kpiStyleDraftFromConfig(
  config: AppKpiDisplayConfig | null | undefined,
): KpiStyleDraft {
  return {
    cardColor: hexOrNull(config?.card_color) ?? "",
    textColor: hexOrNull(config?.text_color) ?? "",
    valueFontSize: kpiFontSizeOf(config?.value_font_size),
    labelFontSize: kpiFontSizeOf(config?.label_font_size),
    rules: storedRules(config).map((rule, index) => ({
      key: index,
      op: rule.op,
      value: String(rule.value),
      valueTo: typeof rule.value_to === "number" ? String(rule.value_to) : "",
      cardColor: hexOrNull(rule.card_color) ?? "",
      textColor: hexOrNull(rule.text_color) ?? "",
    })),
    change: kpiChangeDraftFromConfig(config),
  };
}

function kpiChangeDraftFromConfig(config: AppKpiDisplayConfig | null | undefined): KpiChangeDraft {
  const change = storedChange(config);
  if (change === null) {
    return emptyKpiChangeDraft();
  }
  return {
    column: change.column,
    kind: isChangeKind(change.kind) ? change.kind : KPI_DEFAULT_CHANGE_KIND,
    increaseColor: hexOrNull(change.increase_color) ?? "",
    decreaseColor: hexOrNull(change.decrease_color) ?? "",
    size: kpiFontSizeOf(change.size),
    label: typeof change.label === "string" ? change.label : "",
  };
}

/** A stored or missing size, falling back to the standard one. */
export function kpiFontSizeOf(value: unknown): AppKpiFontSize {
  return isFontSize(value) ? value : KPI_DEFAULT_FONT_SIZE;
}

function parseRuleNumber(text: string): number | null {
  if (text.trim() === "") {
    return null;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function isOptionalColor(value: string): boolean {
  return value === "" || isHexColor(value);
}

export function kpiColorRuleIssues(rule: KpiColorRuleDraft): KpiColorRuleIssue[] {
  const issues: KpiColorRuleIssue[] = [];
  const value = parseRuleNumber(rule.value);
  if (value === null) {
    issues.push("value");
  }
  if (rule.op === "between") {
    const valueTo = parseRuleNumber(rule.valueTo);
    if (valueTo === null) {
      issues.push("valueTo");
    } else if (value !== null && valueTo < value) {
      issues.push("range");
    }
  }
  if (rule.cardColor === "" && rule.textColor === "") {
    issues.push("color");
  }
  return issues;
}

function isCompleteRule(rule: KpiColorRuleDraft): boolean {
  return (
    kpiColorRuleIssues(rule).length === 0 &&
    isOptionalColor(rule.cardColor) &&
    isOptionalColor(rule.textColor)
  );
}

/** Whether the draft can be saved as is — the same checks the server makes. */
export function isKpiStyleDraftValid(draft: KpiStyleDraft): boolean {
  return (
    isOptionalColor(draft.cardColor) &&
    isOptionalColor(draft.textColor) &&
    isOptionalColor(draft.change.increaseColor) &&
    isOptionalColor(draft.change.decreaseColor) &&
    draft.rules.every(isCompleteRule)
  );
}

/**
 * The saved shape: unset keys left out, and the default look as `null`. Anything half-typed (a
 * partial hex, a rule missing its number) is left out too, so the live preview never shows a
 * colour the save would refuse; saving itself is blocked until `isKpiStyleDraftValid`.
 */
export function buildKpiDisplayConfig(draft: KpiStyleDraft): AppKpiDisplayConfig | null {
  const config: AppKpiDisplayConfig = {};
  if (isHexColor(draft.cardColor)) {
    config.card_color = draft.cardColor;
  }
  if (isHexColor(draft.textColor)) {
    config.text_color = draft.textColor;
  }
  if (draft.valueFontSize !== KPI_DEFAULT_FONT_SIZE) {
    config.value_font_size = draft.valueFontSize;
  }
  if (draft.labelFontSize !== KPI_DEFAULT_FONT_SIZE) {
    config.label_font_size = draft.labelFontSize;
  }
  const rules = draft.rules.filter(isCompleteRule).map((rule) => {
    const saved: AppKpiColorRule = { op: rule.op, value: Number(rule.value) };
    if (rule.op === "between") {
      saved.value_to = Number(rule.valueTo);
    }
    if (rule.cardColor !== "") {
      saved.card_color = rule.cardColor;
    }
    if (rule.textColor !== "") {
      saved.text_color = rule.textColor;
    }
    return saved;
  });
  if (rules.length > 0) {
    config.rules = rules;
  }
  const change = buildKpiChange(draft.change);
  if (change !== null) {
    config.change = change;
  }
  return Object.keys(config).length > 0 ? config : null;
}

/** The stored chip, defaults left out as the server stores them; no column means no chip. */
function buildKpiChange(draft: KpiChangeDraft): AppKpiChange | null {
  const column = draft.column.trim();
  if (column === "") {
    return null;
  }
  const change: AppKpiChange = { column };
  if (draft.kind !== KPI_DEFAULT_CHANGE_KIND) {
    change.kind = draft.kind;
  }
  if (isHexColor(draft.increaseColor)) {
    change.increase_color = draft.increaseColor;
  }
  if (isHexColor(draft.decreaseColor)) {
    change.decrease_color = draft.decreaseColor;
  }
  if (draft.size !== KPI_DEFAULT_FONT_SIZE) {
    change.size = draft.size;
  }
  if (draft.label.trim() !== "") {
    change.label = draft.label.trim();
  }
  return change;
}

function ruleMatches(rule: AppKpiColorRule, metric: number): boolean {
  switch (rule.op) {
    case "gt":
      return metric > rule.value;
    case "gte":
      return metric >= rule.value;
    case "lt":
      return metric < rule.value;
    case "lte":
      return metric <= rule.value;
    case "eq":
      return metric === rule.value;
    case "between":
      return (
        typeof rule.value_to === "number" && metric >= rule.value && metric <= rule.value_to
      );
  }
}

function numericValue(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export type KpiCardColors = {
  /** `null` keeps the theme's colour. */
  cardColor: string | null;
  textColor: string | null;
};

/**
 * The colours a card shows for `value`: the first matching rule's, over the base colours. A rule
 * that sets only one colour leaves the other at its base. Text-format cards and non-numeric
 * values skip the rules.
 */
export function resolveKpiCardColors(
  config: AppKpiDisplayConfig | null | undefined,
  value: unknown,
  format: AppKpiFormat,
): KpiCardColors {
  const colors: KpiCardColors = {
    cardColor: hexOrNull(config?.card_color),
    textColor: hexOrNull(config?.text_color),
  };
  const metric = format === "text" ? null : numericValue(value);
  if (metric === null) {
    return colors;
  }
  const matched = storedRules(config).find((rule) => ruleMatches(rule, metric));
  if (matched === undefined) {
    return colors;
  }
  return {
    cardColor: hexOrNull(matched.card_color) ?? colors.cardColor,
    textColor: hexOrNull(matched.text_color) ?? colors.textColor,
  };
}

export type KpiChangeDirection = "up" | "down" | "flat";

/** What the chip shows, before formatting the number for the card. */
export type KpiChangeDisplay = {
  /** Always zero or more; `direction` carries the sign. */
  magnitude: number;
  /** True when `magnitude` is a percentage, so it prints as `9.50%` whatever the card's format. */
  isPercent: boolean;
  direction: KpiChangeDirection;
  /** The colour picked for this direction; `null` keeps the theme's (green up, red down, grey). */
  color: string | null;
  size: AppKpiFontSize;
  label: string | null;
};

/**
 * The card's change chip, or null to show none: no chip set up, a text-format card, or no
 * number to show (the column is missing, not numeric, or `previous` is 0).
 */
export function resolveKpiChange(
  config: AppKpiDisplayConfig | null | undefined,
  value: unknown,
  changeValue: unknown,
  format: AppKpiFormat,
): KpiChangeDisplay | null {
  const change = storedChange(config);
  if (change === null || format === "text") {
    return null;
  }
  const kind = isChangeKind(change.kind) ? change.kind : KPI_DEFAULT_CHANGE_KIND;
  const compared = numericValue(changeValue);
  if (compared === null) {
    return null;
  }
  let delta = compared;
  if (kind === "previous") {
    const current = numericValue(value);
    if (current === null || compared === 0) {
      return null;
    }
    // Against the size of the old value, so a negative base still reads up when it rises.
    delta = ((current - compared) / Math.abs(compared)) * 100;
  }
  const direction: KpiChangeDirection = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const picked = { up: change.increase_color, down: change.decrease_color, flat: null };
  const label =
    typeof change.label === "string" && change.label.trim() !== "" ? change.label : null;
  return {
    magnitude: Math.abs(delta),
    isPercent: kind !== "amount",
    direction,
    color: hexOrNull(picked[direction]),
    size: kpiFontSizeOf(change.size),
    label,
  };
}
